import React, { useState, useMemo, useRef, useEffect } from "react";
import SearchIcon from "@mui/icons-material/Search";
import dayjs from "dayjs";
import DateRangeSelector from "../../../daterangepicker/DateRangeSelector";

// ✅ Status pill styling
const getStatusClasses = (status) => {
  if (!status) return "bg-gray-50 text-gray-600 border border-gray-200";

  switch (status.toLowerCase()) {
    case "success":
      return "bg-[#DCFCE7] text-[#016630]";
    case "failed":
      return "bg-red-50 text-red-600";
    case "pending":
      return "bg-blue-50 text-blue-600";
    default:
      return "bg-gray-50 text-gray-600";
  }
};

const PaymentHistory = ({ paymentshistory = [], isLoading }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 5;

  // ✅ Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDateRange, setSelectedDateRange] = useState({
    startDate: null,
    endDate: null,
    key: "selection",
  });
  const [showCalendar, setShowCalendar] = useState(false);
 const calendarRef = useRef(null);
 useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target) &&
        !event.target.closest("#calendar-toggle-btn") // allow toggle button
      ) {
        setShowCalendar(false);
      }
    };

    if (showCalendar) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCalendar]);
  // ✅ Auto-scroll when calendar opens
  useEffect(() => {
    if (showCalendar && calendarRef.current) {
      calendarRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [showCalendar]);
  // ✅ Handle Apply
  const handleDateApply = () => {
    setShowCalendar(false);
    setCurrentPage(1);
  };

  // ✅ Handle Reset
  const handleDateReset = () => {
    setSelectedDateRange({
      startDate: null,
      endDate: null,
      key: "selection",
    });
    setShowCalendar(false);
    setCurrentPage(1);
  };

  // ✅ Filtered data
const filteredData = useMemo(() => {
  return paymentshistory.filter((p) => {
    // ✅ Date filter
    let passDate = true;

    if (selectedDateRange.startDate && selectedDateRange.endDate) {
      const paymentDate = dayjs(p.created_on);

      passDate = paymentDate.isBetween(
        dayjs(selectedDateRange.startDate).startOf("day"),
        dayjs(selectedDateRange.endDate).endOf("day"),
        null,
        "[]"
      );
    }

    // ✅ Search filter
    let passSearch = true;

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();

      const matchesText =
        p.payment_location?.toLowerCase().includes(term) ||
        p.payment_status?.toLowerCase().includes(term) ||
        p.method?.toLowerCase().includes(term);

      const matchesAmount = !isNaN(term)
        ? Number(p.amount) === Number(term)
        : false;

      passSearch = matchesText || matchesAmount;
    }

    return passDate && passSearch;
  });
}, [paymentshistory, searchTerm, selectedDateRange]);


  // ✅ Pagination
  const totalPages = Math.ceil(filteredData.length / perPage);
  const currentData = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredData.slice(start, start + perPage);
  }, [currentPage, filteredData]);

  return (
    <div className="bg-white rounded-xl shadow p-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-inter font-semibold text-[#101828]">Payment History</h2>
        {/* <span className="px-3 py-1 rounded-full text-xs bg-gray-100 text-gray-600">
          {filteredData.length} Total Payments
        </span> */}
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-4 items-center">
        {/* Date Range Toggle */}
        <div className="relative "ref={calendarRef} >
          <button
            onClick={() => setShowCalendar((prev) => !prev)}
            className="px-4 py-2 border rounded-lg text-sm font-medium bg-white text-gray-700 hover:bg-gray-50 shadow-sm"
          >
            {selectedDateRange.startDate && selectedDateRange.endDate
              ? `${dayjs(selectedDateRange.startDate).format("MM/DD/YYYY")} - ${dayjs(
                  selectedDateRange.endDate
                ).format("MM/DD/YYYY")}`
              : "Select Date Range"}
          </button>

          {showCalendar && (
            <div className="absolute z-50 mt-2" ref={calendarRef}>
              <DateRangeSelector
                onSubmit={handleDateApply}
                handlereset={handleDateReset}
                selectedDateRange={selectedDateRange}
                setSelectedDateRange={setSelectedDateRange}
              />
            </div>
          )}
        </div>

        {/* Search Bar */}
        <div className="flex items-center border rounded-lg px-3 w-full md:w-1/3 bg-white shadow-sm">
          <SearchIcon className="text-gray-500 mr-2" />
          <input
            type="text"
            placeholder="Search by location, status, method, amount..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full py-2 text-sm text-gray-700 focus:outline-none"
          />
        </div>
      </div>

      {/* ✅ Active Filter Chip */}
    {selectedDateRange.startDate && selectedDateRange.endDate && (
  <div className="mb-4 flex justify-start">
    <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full">
      {dayjs(selectedDateRange.startDate).format("MM/DD/YYYY")} -{" "}
      {dayjs(selectedDateRange.endDate).format("MM/DD/YYYY")}
      <button
        onClick={handleDateReset}
        className="ml-2 text-blue-500 hover:text-blue-700"
      >
        ✕
      </button>
    </span>
  </div>
)}


      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-gray-200">
        <table className="w-full text-sm text-left min-h-[60vh]">
          <thead className="bg-[#4a7c8f] text-[#FFFFFF] font-inter text-[12px] uppercase">
            <tr>
              <th className="px-5 py-4 font-medium">Date</th>
              <th className="px-4 py-4 font-medium">Time</th>
              <th className="px-4 py-4 font-medium">Location</th>
              <th className="px-4 py-4 font-medium">Status</th>
              <th className="px-4 py-4 font-medium">Amount</th>
              <th className="px-4 py-4 font-medium">Payment Method</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <tr>
                <td colSpan="6" className="text-center py-6 text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : currentData.length > 0 ? (
              currentData.map((p, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-5 py-3 text-[#101828] font-inter font-normal text-[14px]">{dayjs(p.created_on).format("MM/DD/YYYY")}</td>
                  <td className="px-4 py-3 text-[#101828] font-inter font-normal text-[14px]">{p.created_at}</td>
                  <td className="px-4 py-3 text-[#101828] font-inter font-normal text-[14px]">{p.payment_location}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-1 text-xs font-semibold font-inter rounded-full ${getStatusClasses(
                        p.payment_status
                      )}`}
                    >
                      {p.payment_status.charAt(0).toUpperCase() + p.payment_status.slice(1)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#101828] font-inter font-normal text-[14px]">
                    ${parseFloat(p.amount).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-[#101828] font-inter font-normal text-[14px]">{p.method}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="text-center py-6 text-gray-500">
                  No payment history found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-4">
          <button
            className="px-3 py-1 text-sm border rounded-lg disabled:opacity-50"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            Prev
          </button>
          <span className="text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="px-3 py-1 text-sm border rounded-lg disabled:opacity-50"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default PaymentHistory;
